/**
 * @file src/components/IdleTimer.jsx
 * @description Tracks user inactivity and triggers logout when idle or session expires.
 */

import axios from "axios";
import PropTypes from "prop-types";
import { useEffect, useRef } from "react";
import { notifyUser } from "../utils/notifyUser";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IdleTimer = ({
    idleMinutes = 15,
    checkInterval = 60,
    logoutRedirect = "/login",
}) => {
    const lastActivity = useRef(Date.now());
    const timerRef = useRef(null);

    // --- Helper: force logout ---
    const handleLogout = async (reason = "idle") => {
        try {
            await axios.post(
                `${API_URL}/api/auth/logout`,
                {},
                { withCredentials: true },
            );
        } catch{
            // intentionally ignore; we still want to clear UI state
        }

        localStorage.removeItem("userLoggedIn");
        localStorage.removeItem("session_expiry");
        localStorage.removeItem("last_session_sync");

        notifyUser(null, "warning", reason === "server"
                ? "Your session expired on the server. Please log in again."
                : "You have been logged out due to inactivity.", "toast", null, null, null, null, null, false, false, "top-center");

        setTimeout(() => {
            window.location.href = logoutRedirect;
        }, 1500);
    };

    // --- Helper: reset inactivity timer ---
    const resetTimer = () => {
        lastActivity.current = Date.now();
    };

    // --- 1. Monitor user activity ---
    useEffect(() => {
        const events = [
            "mousemove",
            "mousedown",
            "keypress",
            "scroll",
            "touchstart",
        ];

        events.forEach((e) => window.addEventListener(e, resetTimer));

        return () => {
            events.forEach((e) => window.removeEventListener(e, resetTimer));
        };
    }, []);

    // --- 2. Check for local inactivity ---
    useEffect(() => {
        const checkIdle = () => {
            const now = Date.now();
            const diffMinutes = (now - lastActivity.current) / (1000 * 60);
            if(diffMinutes >= idleMinutes) {
                handleLogout("idle");
            }
        };

        timerRef.current = setInterval(checkIdle, checkInterval * 1000);

        return () => clearInterval(timerRef.current);
    }, [idleMinutes, checkInterval]);

    // --- 3. Listen for session-expired from backend polling ---
    useEffect(() => {
        const handleServerExpire = (event) => {
            console.warn("Server reported expired session:", event.detail);
            handleLogout("server");
        };

        window.addEventListener("session-expired", handleServerExpire);

        return () => {
            window.removeEventListener("session-expired", handleServerExpire);
        };
    }, []);

    // --- 4. Listen for session-synced to extend local timers ---
    useEffect(() => {
        const handleSessionSynced = (event) => {
            const { expiry } = event.detail || {};
            if(expiry) {
                console.debug(`Session synced, expires at ${expiry}`);
            }
            resetTimer();
        };

        window.addEventListener("session-synced", handleSessionSynced);

        return () => {
            window.removeEventListener("session-synced", handleSessionSynced);
        };
    }, []);

    return null;
};

IdleTimer.propTypes = {
    idleMinutes: PropTypes.number,
    checkInterval: PropTypes.number,
    logoutRedirect: PropTypes.string,
};
export default IdleTimer;
